import { useQueryClient } from '@tanstack/react-query';
import { Save, UserRound } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import type { MeDTO } from '../../../shared/dto/auth';
import type { ProfileUpdateRequest } from '../../../shared/dto/members';
import { looksLikeEmail } from '../../../shared/email';
import { useUpdateProfile } from '../../api/auth';
import { invalidateMembers } from '../../api/members';
import AvatarEditor from '../../components/AvatarEditor';
import { SectionBadge } from '../../components/people';
import { useToast } from '../../components/toast';
import { Button, Switch, TextInput } from '../../components/ui';
import { useT } from '../../i18n';
import { errorText, invalidFields } from './errorText';
import { SettingsSection } from './parts';

/** Same rule as the server (routes/members.ts normalizePhone). Empty is allowed. */
export function phoneLooksValid(raw: string): boolean {
  const s = raw.replace(/\s+/g, ' ').trim();
  if (!s) return true;
  return s.length <= 40 && /^\+?[0-9(][0-9 ()\-.]*$/.test(s) && /\d/.test(s);
}

interface Draft {
  fullName: string;
  phone: string;
  emailPublic: string;
}

const draftFrom = (me: MeDTO): Draft => ({ fullName: me.fullName, phone: me.phone ?? '', emailPublic: me.emailPublic ?? '' });

export function ProfileSection({ me }: { me: MeDTO }) {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const update = useUpdateProfile();
  const [draft, setDraft] = useState<Draft>(() => draftFrom(me));
  const [touched, setTouched] = useState(false);
  const [serverInvalid, setServerInvalid] = useState<Set<string>>(new Set());
  const [shareBusy, setShareBusy] = useState<'phone' | 'email' | null>(null);

  // Pick up changes made elsewhere (another device, onboarding) while the form is untouched.
  useEffect(() => {
    if (!touched) setDraft(draftFrom(me));
  }, [me, touched]);

  const nameError = draft.fullName.trim() === '' ? t('settings.name_required') : null;
  const phoneError = !phoneLooksValid(draft.phone) || serverInvalid.has('phone') ? t('settings.phone_invalid') : null;
  const emailError =
    (draft.emailPublic.trim() !== '' && !looksLikeEmail(draft.emailPublic.trim())) || serverInvalid.has('emailPublic')
      ? t('settings.email_invalid')
      : null;

  const patch: ProfileUpdateRequest = {};
  if (draft.fullName.trim() !== me.fullName) patch.fullName = draft.fullName.trim();
  if ((draft.phone.trim() || null) !== me.phone) patch.phone = draft.phone.trim() || null;
  if ((draft.emailPublic.trim() || null) !== me.emailPublic) patch.emailPublic = draft.emailPublic.trim() || null;
  const dirty = Object.keys(patch).length > 0;

  const edit = (k: keyof Draft, v: string) => {
    setTouched(true);
    setServerInvalid(new Set());
    setDraft((d) => ({ ...d, [k]: v }));
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!dirty || nameError || phoneError || emailError) return;
    try {
      await update.mutateAsync(patch);
      setTouched(false);
      toast.show(t('settings.profile_saved'), { tone: 'ok' });
    } catch (err) {
      const bad = invalidFields(err);
      if (bad.size > 0) setServerInvalid(bad);
      else toast.show(errorText(t, err), { tone: 'error' });
    }
  };

  const toggleShare = async (which: 'phone' | 'email', value: boolean) => {
    setShareBusy(which);
    try {
      await update.mutateAsync(which === 'phone' ? { sharePhone: value } : { shareEmail: value });
    } catch (err) {
      toast.show(errorText(t, err), { tone: 'error' });
    } finally {
      setShareBusy(null);
    }
  };

  const hasPhone = !!(me.phone || draft.phone.trim());

  return (
    <SettingsSection icon={UserRound} title={t('settings.profile')} id="settings-profile">
      <div className="flex flex-col gap-5 p-5">
        <div className="flex flex-col items-center gap-2">
          <AvatarEditor me={me} onSaved={() => void invalidateMembers(qc, me.id)} />
        </div>

        <form onSubmit={(e) => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
          <TextInput
            label={t('settings.full_name')}
            value={draft.fullName}
            onChange={(e) => edit('fullName', e.target.value)}
            autoComplete="name"
            autoCapitalize="words"
            maxLength={100}
            error={touched ? nameError : null}
            required
          />
          <TextInput
            label={t('settings.phone')}
            value={draft.phone}
            onChange={(e) => edit('phone', e.target.value)}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            maxLength={40}
            placeholder={t('settings.phone_hint')}
            error={phoneError}
          />
          <TextInput
            label={t('settings.email_public')}
            value={draft.emailPublic}
            onChange={(e) => edit('emailPublic', e.target.value)}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={254}
            hint={t('settings.email_public_hint', { email: me.email })}
            error={emailError}
          />
          <div className="flex flex-wrap items-center justify-end gap-3">
            {dirty ? <span className="text-[15px] font-medium text-[var(--warn-text)]">{t('settings.unsaved')}</span> : null}
            <Button type="submit" icon={Save} busy={update.isPending && shareBusy === null} disabled={!dirty || !!nameError || !!phoneError || !!emailError}>
              {t('common.save')}
            </Button>
          </div>
        </form>

        <div className="border-t border-[var(--line)] pt-3">
          <Switch
            label={t('settings.share_phone')}
            checked={shareBusy === 'phone' ? !me.sharePhone : me.sharePhone}
            disabled={shareBusy !== null || (!hasPhone && !me.sharePhone)}
            description={!hasPhone ? t('settings.share_needs_phone') : undefined}
            onChange={(v) => void toggleShare('phone', v)}
          />
          <Switch
            label={t('settings.share_email')}
            checked={shareBusy === 'email' ? !me.shareEmail : me.shareEmail}
            disabled={shareBusy !== null}
            description={me.emailPublic ?? me.email}
            onChange={(v) => void toggleShare('email', v)}
          />
          <p className="pt-1 text-[15px] text-[var(--muted)]">{t('settings.share_hint')}</p>
        </div>

        <div className="flex flex-col gap-1 border-t border-[var(--line)] pt-4">
          <span className="text-[15px] font-semibold text-[var(--ink-2)]">{t('settings.section_role')}</span>
          <div className="flex flex-wrap items-center gap-2">
            {me.section ? <SectionBadge section={me.section} /> : <span>{t('section.none')}</span>}
            <span className="text-[var(--ink-2)]">· {t(`role.${me.role}`)}</span>
            {me.title ? <span className="text-[var(--ink-2)]">· {me.title}</span> : null}
          </div>
          <p className="text-[14px] text-[var(--muted)]">{t('settings.section_hint')}</p>
          <div className="mt-2 text-[15px]">
            <span className="text-[var(--muted)]">{t('settings.login_email')}: </span>
            <span className="break-all">{me.email}</span>
          </div>
        </div>
      </div>
    </SettingsSection>
  );
}
